import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IApiParams,
  IError,
  IMixId,
  IMixIdCategorization,
  IResponseData,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';
import { IQueryParams } from '../services';

const mixIdsQueryKey = 'getMixIds';
const mixIdCategorizationsQueryKey = 'getMixIdCategorizations';
const mixIdSearchCategorizationsQueryKey = 'searchMixIdCategorizations';
const updateMixIdCategorizationKey = 'updateMixIdCategorization';
const defaultCount = 0;
const defaultSkip = 0;

export function useMixDesignApi({
  queryParameters,
  organizationId,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();
  const [mixIds, setMixIds] = useState<IMixId[]>([]);

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getMixIdsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/MixDesign/get-mix-ids`;
  }, [currentSupplierId]);

  const getMixDesignCategorizationsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/MixDesign/get-mix-design-categorizations`;
  }, [currentSupplierId]);

  const updateMixIdCategorizationUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/MixDesign/update-mix-design-categorizations`;
  }, [currentSupplierId]);

  const {
    data: mixIdsResponse,
    refetch: fetchMixIds,
    isLoading: isFetchingMixIds,
    error: fetchMixIdsError,
  } = useQueryWithErrorHandler<IResponseData<IMixId[]>, IError>({
    queryKey: [mixIdsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getMixIdsUrl}`, {
        method: 'GET',
        queryParameters,
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getMixIds, error, {
        getMixIds: getMixIdsUrl,
      });
      openModal(error);
    },

    placeholderData: keepPreviousData,
  });

  const useMixIdCategorizationQuery = (
    queryKey: string,
    url: string,
    queryParams?: IQueryParams,
  ) => {
    return useQueryWithErrorHandler<
      IResponseData<IMixIdCategorization[]>,
      IError
    >({
      queryKey: [queryKey, { queryParams }],

      queryFn: async () => {
        return await authClient(`${url}`, {
          queryParameters: queryParams,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        firebaseAnalytics.logErrorEvent(
          errors.getMixDesignCategorizations,
          error,
          {
            getMixId: getMixIdsUrl,
          },
        );
        openModal(error);
      },
    });
  };

  const {
    data: mixIdCategorizationsResponse,
    refetch: fetchMixIdCategorizations,
    isLoading: isFetchingMixIdCategorizations,
    error: fetchMixIdCategorizationsError,
  } = useMixIdCategorizationQuery(
    mixIdCategorizationsQueryKey,
    getMixDesignCategorizationsUrl,
    queryParameters,
  );

  const {
    data: searchMixIdCategorizationsResponse,
    refetch: fetchSearchMixIdCategorizations,
    isLoading: isFetchingSearchMixIdCategorizations,
    error: fetchMixIdSearchCategorizationsError,
  } = useMixIdCategorizationQuery(
    mixIdSearchCategorizationsQueryKey,
    getMixDesignCategorizationsUrl,
    queryParameters,
  );

  const {
    mutateAsync: updateMixDesignCategorization,
    isPending: isUpdatingMixDesignCategorization,
  } = useMutation({
    mutationFn: (payload: IMixIdCategorization[]) =>
      authClient(updateMixIdCategorizationUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [updateMixIdCategorizationKey],
      });
      return queryClient.getQueryData<IResponseData<IMixIdCategorization[]>>([
        updateMixIdCategorizationKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IMixIdCategorization[]>>(
          [updateMixIdCategorizationKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeOverallNotificationsStatus,
        error,
        {
          updateMixIdCategorizationUrl,
          ...variables,
        },
      );
      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateMixIdCategorizationKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [mixIdCategorizationsQueryKey],
      });
    },
  });

  useEffect(() => {
    if (!isFetchingMixIds) {
      setMixIds((prevState: IMixId[]) => {
        return queryParameters?.skip !== defaultSkip
          ? mixIdsResponse?.data
            ? [...prevState, ...mixIdsResponse.data]
            : prevState
          : mixIdsResponse?.data || [];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixIdsResponse?.data]);

  const mixIdCategorization = useMemo<IMixIdCategorization[] | undefined>(
    () => mixIdCategorizationsResponse?.data,
    [mixIdCategorizationsResponse],
  );

  const mixIdCategorizationTotalCount = useMemo<number>(
    () => mixIdCategorizationsResponse?.metadata?.totalCount || defaultCount,
    [mixIdCategorizationsResponse],
  );

  const searchMixIdCategorization = useMemo<IMixIdCategorization[] | undefined>(
    () => searchMixIdCategorizationsResponse?.data,
    [searchMixIdCategorizationsResponse],
  );

  return {
    mixIdsResponse,
    mixIds,
    fetchMixIds,
    isFetchingMixIds,
    fetchMixIdsError,
    mixIdCategorization,
    fetchMixIdCategorizations,
    isFetchingMixIdCategorizations,
    fetchMixIdCategorizationsError,
    mixIdCategorizationTotalCount,
    updateMixDesignCategorization,
    isUpdatingMixDesignCategorization,
    searchMixIdCategorization,
    fetchSearchMixIdCategorizations,
    isFetchingSearchMixIdCategorizations,
    fetchMixIdSearchCategorizationsError,
  };
}
